/* ErrorBoundary — catch React tree crashes and show recovery UI.
 *
 * Why this exists
 * ───────────────
 * React renders are all-or-nothing: if a single component throws during
 * render, useEffect, or event handler, React unmounts the whole subtree
 * by default. Without a boundary in place, that translates to a totally
 * blank white screen for the user — no header, no nav, no message.
 * They are then stuck unless they manually refresh or guess to clear
 * cache. For a paid SaaS aimed at lawyers, that single bad render can
 * eat hours of trust before we even hear about it.
 *
 * What this catches
 * ─────────────────
 * • Throw in render() or hook bodies
 * • Throw in lifecycle methods
 *
 * What this does NOT catch (limitations of React error boundaries)
 * ──────────────────────────────────────────────────────────────
 * • Async errors inside event handlers (fetch().then(throw) — those
 *   must be try/catch'd or use .catch())
 * • setTimeout / setInterval callbacks
 * • Server-side rendering errors (we're CSR-only anyway)
 *
 * Strategy
 * ────────
 * We wrap the whole <App/> tree once in `App.js` and show a tasteful
 * recovery card matching the LexIQ palette. The `onReset` button clears
 * local state and triggers a soft reload (`window.location.reload()`)
 * which is the most reliable way to recover from a corrupted React
 * tree in practice — far more dependable than trying to "patch" state.
 *
 * Sentry / observability
 * ──────────────────────
 * `componentDidCatch` is the hook Sentry's React integration latches
 * onto. When Sentry SDK is added (planned P1), errors here will
 * auto-surface with full stack + breadcrumbs without code changes.
 */
import { Component } from "react";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true, errorId: Date.now().toString(36) };
  }

  componentDidCatch(error, info) {
    // Console first — visible in browser DevTools and Capacitor logs.
    // Once Sentry SDK is initialised at app boot, this same throw is
    // already captured by Sentry's auto-instrumentation; no extra
    // bridge code needed.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReset = () => {
    // A hard reload is the only reliable way to recover from a
    // corrupted React tree — `this.setState` is dangerous because
    // the offending component may re-throw immediately on remount.
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        dir="rtl"
        className="min-h-screen flex items-center justify-center px-6 bg-[#fafafa]"
        data-testid="error-boundary-root"
      >
        <div className="max-w-md w-full text-center p-8 rounded-2xl bg-white border border-stone-200 shadow-sm">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-amber-50 flex items-center justify-center text-2xl">
            ⚠️
          </div>
          <h1 className="font-amiri text-2xl text-[#0A192F] mb-3" data-testid="error-boundary-title">
            حَدَثَ خَللٌ غَير مُتَوَقَّع
          </h1>
          <p className="text-sm text-stone-600 leading-relaxed mb-6">
            نَعتَذِر — وَقَع خَطأ تِقني في هذِه الصَّفحة. تَمَّ تَسجيل
            الحالة وَالفَريق سَيُعالِجها قَريباً. يُمكِنُك إِعادة المُحاوَلة
            أَو العَودة إِلى الرَّئيسيّة.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              type="button"
              onClick={this.handleReset}
              className="px-5 py-2.5 rounded-lg bg-[#0A192F] text-white text-sm hover:bg-[#1a2a40] transition"
              data-testid="error-boundary-retry-btn"
            >
              إعادة المحاولة
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="px-5 py-2.5 rounded-lg bg-white border border-stone-300 text-[#0A192F] text-sm hover:bg-stone-50 transition"
              data-testid="error-boundary-home-btn"
            >
              الصفحة الرئيسية
            </button>
          </div>
          {this.state.errorId && (
            <div className="mt-6 text-[10px] text-stone-400" data-testid="error-boundary-id">
              معرِّف الخطأ: {this.state.errorId}
            </div>
          )}
        </div>
      </div>
    );
  }
}
